// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useMemo, useState } from 'react';
import {
  Box, Tabs, Tab, Typography, Stack, Chip, Alert,
} from '@mui/material';
import UserAdmin from './UserAdmin';
import ListsAdmin from './ListsAdmin';
import CustomerAdmin from './CustomerAdmin';
import FieldRequests from './FieldRequests';
import FeedbackAdmin from './FeedbackAdmin';
import TrashAdmin from './TrashAdmin';
import { getSession } from './auth';

// One place for the things that govern the product rather than describe an
// estate. These used to be four unrelated entries in a Manage menu of twenty,
// which made "who can do what" impossible to see in one go.
//
// The split that matters is between the two middle tabs:
//   Fields & keys  -> changes the SHAPE of every fact sheet. Governed.
//   Lists & values -> changes what may be CHOSEN in an existing field. Not.

export default function AdminPortal({ tenants = [], tenantId, onTenantsChanged }) {
  const me = getSession();
  const iAmMaster = !!me && (me.groups || []).includes('OptimumAdmins');
  const iAmAdmin = iAmMaster || (me && me.roleId === 'admin');

  const tabs = useMemo(() => {
    const list = [];
    // User administration is admin-only at the API, so offering the tab to a
    // viewer would just render an error panel.
    if (iAmAdmin) list.push({ key: 'users', label: 'Users', render: () => <UserAdmin tenants={tenants} /> });
    list.push({ key: 'fields', label: 'Fields & keys', render: () => <FieldsTab admin={iAmAdmin} tenantId={tenantId} /> });
    list.push({
      key: 'values',
      label: 'Lists & values',
      render: () => <ListsAdmin tenantId={tenantId} sections={['picklists', 'tags', 'vendors']} />,
    });
    // Everyone can SEND feedback; reading it is scoped by RLS to your own
    // tenant, and only an admin can triage. A client admin seeing their own
    // people's reports is the point — it is their workspace.
    list.push({ key: 'feedback', label: 'Feedback', render: () => <FeedbackAdmin /> });
    // Deleting is reversible for 30 days, which is only true in practice if
    // there is somewhere to see what is in the trash. Offered to any admin
    // because it is their own workspace's history; restoring inside it is
    // master-only, at the API and in the database, not merely in the UI.
    if (iAmAdmin) {
      list.push({
        key: 'trash',
        label: 'Deleted records',
        render: () => <TrashAdmin tenants={tenants} tenantId={tenantId} />,
      });
    }
    // Tenant administration is the master's alone — a client admin managing the
    // client roster would be able to see who else Optimum works with.
    if (iAmMaster) {
      list.push({ key: 'customers', label: 'Customers', render: () => <CustomerAdmin onChanged={onTenantsChanged} /> });
    }
    return list;
  }, [iAmAdmin, iAmMaster, tenants, tenantId, onTenantsChanged]);

  const [tab, setTab] = useState(0);
  const active = tabs[Math.min(tab, tabs.length - 1)];

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#021F4B' }}>Administration</Typography>
        <Chip size="small" label={iAmMaster ? 'Optimum master — all tenants' : `${(me && me.tenantName) || 'your workspace'} only`} />
      </Stack>

      <Tabs value={Math.min(tab, tabs.length - 1)} onChange={(e, v) => setTab(v)}
        sx={{ mb: 1, borderBottom: '1px solid #E0E6ED', '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}>
        {tabs.map((t) => <Tab key={t.key} label={t.label} />)}
      </Tabs>

      {active ? active.render() : (
        <Alert severity="info" sx={{ mt: 2 }}>
          Your account does not have administrative access to this workspace.
        </Alert>
      )}
    </Box>
  );
}

// Keys are two things at once: the queue of what people have asked for, and the
// definitions that already exist. Showing them apart would mean an admin
// approving a field without seeing what is already there to duplicate.
function FieldsTab({ admin, tenantId }) {
  return (
    <>
      <FieldRequests />
      {admin && (
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #E0E6ED' }}>
          <ListsAdmin tenantId={tenantId} sections={['fields']} />
        </Box>
      )}
    </>
  );
}
